/**
 * Hook + provider for the live WS stream. See module doc in
 * `useLiveStream.ts` shim (left in place to avoid file deletes).
 */
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";

import { LiveSocket, type LiveSocketState } from "../api/ws";
import type { BotStatus, LiveEvent, LiveWindow } from "../api/schemas";
import { useMarketId } from "./useMarket";

const DEFAULT_MARKET_ID = "btc-5m";

/**
 * Read the market id off a live event envelope. Events from V2 / legacy
 * sources omit the field; we treat those as `btc-5m` so V2 datasets keep
 * flowing into the default partition.
 */
function eventMarketId(evt: LiveEvent): string {
  const explicit = (evt as { market_id?: unknown }).market_id;
  if (typeof explicit === "string" && explicit) return explicit;
  if (evt.event_type === "live.bot_status" && evt.payload.active_window) {
    const aw = evt.payload.active_window as Record<string, unknown>;
    const fromAw = aw.market_id;
    if (typeof fromAw === "string" && fromAw) return fromAw;
  }
  return DEFAULT_MARKET_ID;
}

export interface LiveDecisionPerMinute {
  minute: number;
  open: string | null;
  close: string | null;
  delta: string;
  dir: "UP" | "DOWN" | "FLAT";
  samples: number;
  is_live: boolean;
  /**
   * True when this candle was in the gate's
   * ``qualifying_minutes[-candles_to_check:]`` tail -- i.e. the bot
   * actually used it to compute the matching-candles count. Older
   * builds that emit ``per_minute`` for only the evaluated tail are
   * normalised to ``evaluated: true`` by the parser below so legacy
   * payloads still render correctly.
   */
  evaluated: boolean;
  matches: boolean;
}

export interface LiveDecisionCheck {
  name: string;
  passed: boolean;
  value?: unknown;
  threshold?: unknown;
  detail?: string | null;
  per_minute?: LiveDecisionPerMinute[] | null;
  direction_evaluated?: "UP" | "DOWN" | null;
}

export interface LiveDecisionRow {
  ts: string;
  slug: string;
  outcome: string;
  direction: string | null;
  favorite_odds: string | null;
  matching_candles: number | null;
  candles_evaluated: number | null;
  block_reason: string | null;
  checks: LiveDecisionCheck[];
}
export interface LiveOrderRow {
  ts: string;
  slug: string;
  order_id: string;
  phase: string;
  direction: string;
  price: string | null;
  pnl_usdc: string | null;
}

export interface LivePriceTickRow {
  ts: string;
  slug: string;
  secs_left: number;
  btc_price: string;
  mode: "live" | "paper" | null;
  feed_health: string | null;
}

export interface LiveOddsTickRow {
  ts: string;
  slug: string;
  up_bid: string | null;
  up_ask: string | null;
  down_bid: string | null;
  down_ask: string | null;
  mode: "live" | "paper" | null;
  feed_health: string | null;
}

export interface LiveSnapshot {
  socket: LiveSocketState;
  botStatus: BotStatus | null;
  currentWindow: LiveWindow | null;
  priceTicks: LivePriceTickRow[];
  oddsTicks: LiveOddsTickRow[];
  decisions: LiveDecisionRow[];
  orders: LiveOrderRow[];
  isOffline: boolean;
}

const DEFAULTS: LiveSnapshot = {
  socket: { connected: false, reconnectAttempt: 0, lastEventTs: null, droppedInvalid: 0, lastError: null },
  botStatus: null,
  currentWindow: null,
  priceTicks: [],
  oddsTicks: [],
  decisions: [],
  orders: [],
  isOffline: true,
};

const RING_SIZE = 30;
// Sized to comfortably hold every per-second tick of the longest
// supported market window (4h = 14400 ticks) plus headroom for the
// odd skipped/synthetic tick. The legacy value (600) was sized for
// 5-minute windows only; on 15m / 1h / 4h markets it caused the WS
// ring to evict the oldest ticks mid-window, which then made the
// dashboard's UI-derived "minute candle" open/close/Δ values shift
// retroactively as new ticks pushed older ones out of the ring. The
// in-memory cost is ~50 bytes per tick × 16384 ≈ 800 KiB per ring,
// which is acceptable for the active window's snapshot.
const PRICE_RING_SIZE = 16_384;
const STALE_MS = 15_000;
const LIVE_CACHE_KEY = "poly-bot-v2-live-snapshot";

const Ctx = createContext<LiveSnapshot>(DEFAULTS);

type PersistedLiveSnapshot = {
  botStatus: BotStatus | null;
  currentWindow: LiveWindow | null;
  priceTicks: LivePriceTickRow[];
  oddsTicks: LiveOddsTickRow[];
};

function readPersistedSnapshot(): PersistedLiveSnapshot | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(LIVE_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedLiveSnapshot>;
    return {
      botStatus: parsed.botStatus ?? null,
      currentWindow: parsed.currentWindow ?? null,
      priceTicks: Array.isArray(parsed.priceTicks) ? parsed.priceTicks.slice(0, PRICE_RING_SIZE) : [],
      oddsTicks: Array.isArray(parsed.oddsTicks) ? parsed.oddsTicks.slice(0, PRICE_RING_SIZE) : [],
    };
  } catch {
    return null;
  }
}

function writePersistedSnapshot(next: PersistedLiveSnapshot): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(LIVE_CACHE_KEY, JSON.stringify(next));
  } catch {
    // no-op
  }
}

export function LiveStreamProvider({
  children, url,
}: { children: ReactNode; url: string }) {
  const persisted = useMemo(() => readPersistedSnapshot(), []);
  const [snap, setSnap] = useState<LiveSnapshot>({
    ...DEFAULTS,
    botStatus: persisted?.botStatus ?? DEFAULTS.botStatus,
    currentWindow: persisted?.currentWindow ?? DEFAULTS.currentWindow,
    priceTicks: persisted?.priceTicks ?? DEFAULTS.priceTicks,
    oddsTicks: persisted?.oddsTicks ?? DEFAULTS.oddsTicks,
  });
  const ringDecisions = useRef<LiveDecisionRow[]>([]);
  const ringOrders = useRef<LiveOrderRow[]>([]);
  const ringPrices = useRef<LivePriceTickRow[]>(persisted?.priceTicks ?? []);
  const ringOdds = useRef<LiveOddsTickRow[]>(persisted?.oddsTicks ?? []);
  const lastWindow = useRef<LiveWindow | null>(persisted?.currentWindow ?? null);
  // FE-2: guard against leaking a previous LiveSocket if the effect
  // below re-runs (e.g. `url` changes) — close the old one first.
  const socketRef = useRef<LiveSocket | null>(null);

  // V3 multi-market: the global snapshot only reflects events for the
  // active market. A ref mirror of `marketId` lets the WS callback see
  // the latest value without re-creating the LiveSocket every switch.
  const marketId = useMarketId();
  const activeMarketIdRef = useRef<string>(marketId);

  // Reset all rings and the snapshot when the operator switches market.
  // Each market keeps its own view; we don't try to back-fill from a
  // different market's events.
  useEffect(() => {
    activeMarketIdRef.current = marketId;
    ringDecisions.current = [];
    ringOrders.current = [];
    ringPrices.current = [];
    ringOdds.current = [];
    lastWindow.current = null;
    setSnap((prev) => ({
      ...prev,
      botStatus: null,
      currentWindow: null,
      priceTicks: [],
      oddsTicks: [],
      decisions: [],
      orders: [],
    }));
  }, [marketId]);

  useEffect(() => {
    const handle = (evt: LiveEvent) => {
      // Filter events to the currently-selected market. Events that omit
      // market_id (V2-shape) are treated as btc-5m so V2 deployments keep
      // populating the default selection without changes.
      if (eventMarketId(evt) !== activeMarketIdRef.current) return;
      setSnap((prev) => updateOnEvent(prev, evt, ringDecisions, ringOrders, ringPrices, ringOdds, lastWindow));
    };
    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
    }
    const sock = new LiveSocket({
      url,
      onEvent: handle,
      onState: (s) => setSnap((prev) => ({ ...prev, socket: s, isOffline: computeOffline(s) })),
    });
    socketRef.current = sock;
    sock.connect();
    const interval = setInterval(() => {
      setSnap((prev) => ({ ...prev, isOffline: computeOffline(prev.socket) }));
    }, 2_000);
    return () => {
      clearInterval(interval);
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [url]);

  useEffect(() => {
    writePersistedSnapshot({
      botStatus: snap.botStatus,
      currentWindow: snap.currentWindow,
      priceTicks: snap.priceTicks,
      oddsTicks: snap.oddsTicks,
    });
  }, [snap.botStatus, snap.currentWindow, snap.priceTicks, snap.oddsTicks]);

  const value = useMemo(() => snap, [snap]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useLiveSnapshot(): LiveSnapshot {
  return useContext(Ctx);
}

function computeOffline(s: LiveSocketState): boolean {
  if (!s.connected) return true;
  if (!s.lastEventTs) return true;
  const ageMs = Date.now() - new Date(s.lastEventTs).getTime();
  return ageMs > STALE_MS;
}

function didWindowStartAdvance(prevStart: string, nextStart: string): boolean {
  const prevMs = Date.parse(prevStart);
  const nextMs = Date.parse(nextStart);
  return Number.isFinite(prevMs) && Number.isFinite(nextMs) && nextMs > prevMs;
}

function inferWindowBoundsFromTick(ts: string, secsLeft: number): { window_start: string; window_end: string } {
  const nowMs = Date.parse(ts);
  if (!Number.isFinite(nowMs)) {
    return { window_start: ts, window_end: ts };
  }
  // V3 multi-market: cap secs_left at 4h (the longest supported market
  // window). We don't know the market's window length here so the start
  // is a best-effort estimate using `secs_left` rounded up to the next
  // 5-minute boundary; the next bot_status will overwrite with the
  // authoritative bounds anyway.
  const MAX_WINDOW_SECONDS = 14400;
  const left = Math.max(0, Math.min(MAX_WINDOW_SECONDS, Math.floor(secsLeft)));
  const endMs = nowMs + (left * 1000);
  // Choose the smallest standard window length that fully contains
  // `secs_left`: 5m=300, 15m=900, 1h=3600, 4h=14400. This keeps the
  // 5-min default for legacy callers while still letting the 15m/1h/4h
  // dashboards show a plausible window_start until bot_status arrives.
  const STANDARD_LENGTHS = [300, 900, 3600, 14400];
  const totalSeconds = STANDARD_LENGTHS.find((s) => s >= left) ?? 300;
  const startMs = endMs - (totalSeconds * 1000);
  return {
    window_start: new Date(startMs).toISOString(),
    window_end: new Date(endMs).toISOString(),
  };
}

function updateOnEvent(
  prev: LiveSnapshot,
  evt: LiveEvent,
  decRing: { current: LiveDecisionRow[] },
  ordRing: { current: LiveOrderRow[] },
  priceRing: { current: LivePriceTickRow[] },
  oddsRing: { current: LiveOddsTickRow[] },
  lastWindow: { current: LiveWindow | null },
): LiveSnapshot {
  let botStatus = prev.botStatus;
  let currentWindow = prev.currentWindow;
  if (evt.event_type === "live.bot_status") {
    botStatus = evt.payload;
    if (evt.payload.active_window) {
      const aw = evt.payload.active_window;
      const prevWindow = lastWindow.current;
      const sameSlug = Boolean(prevWindow && prevWindow.slug === aw.slug);
      const rolled = Boolean(
        prevWindow
        && sameSlug
        && didWindowStartAdvance(prevWindow.window_start, aw.window_start),
      );
      if (rolled) {
        priceRing.current = [];
        oddsRing.current = [];
        decRing.current = [];
      }
      const lw: LiveWindow = sameSlug && prevWindow && !rolled
        ? {
            ...prevWindow,
            window_start: aw.window_start,
            window_end: aw.window_end,
          }
        : {
            slug: aw.slug,
            window_start: aw.window_start,
            window_end: aw.window_end,
            secs_left: 0,
            btc_price: "0",
            odds: { up: { bid: null, ask: null }, down: { bid: null, ask: null } },
            config_version_id: evt.config_version_id ?? prevWindow?.config_version_id ?? "",
            active_order_id: null,
          };
      lastWindow.current = lw;
      currentWindow = lw;
    }
  } else if (evt.event_type === "live.price_tick") {
    const priceRow: LivePriceTickRow = {
      ts: evt.ts,
      slug: evt.slug,
      secs_left: evt.payload.secs_left,
      btc_price: evt.payload.btc_price,
      mode: evt.payload.mode ?? null,
      feed_health: evt.payload.feed_health ?? null,
    };
    const prevPrices = priceRing.current;
    const prevPrice = prevPrices[0] ?? null;
    const rolledBySecsLeft = Boolean(
      prevPrice
      && prevPrice.slug === evt.slug
      && evt.payload.secs_left > (prevPrice.secs_left + 5),
    );
    const sameWindow = prevPrices.length === 0 || (prevPrice?.slug === evt.slug && !rolledBySecsLeft);
    priceRing.current = (sameWindow ? [priceRow, ...prevPrices] : [priceRow]).slice(0, PRICE_RING_SIZE);
    if (rolledBySecsLeft) {
      oddsRing.current = [];
      decRing.current = [];
    }

    const inferredBounds = inferWindowBoundsFromTick(evt.ts, evt.payload.secs_left);
    const lw = lastWindow.current && lastWindow.current.slug === evt.slug && !rolledBySecsLeft
      ? lastWindow.current
      : {
          slug: evt.slug,
          window_start: inferredBounds.window_start,
          window_end: inferredBounds.window_end,
          secs_left: evt.payload.secs_left,
          btc_price: evt.payload.btc_price,
          odds: { up: { bid: null, ask: null }, down: { bid: null, ask: null } },
          config_version_id: evt.config_version_id ?? "",
          active_order_id: null,
        };
    const updated: LiveWindow = {
      ...lw,
      btc_price: evt.payload.btc_price,
      secs_left: evt.payload.secs_left,
      config_version_id: evt.config_version_id ?? lw.config_version_id,
    };
    if (evt.payload.mode) {
      botStatus = {
        ...(botStatus ?? { state: "running", mode: evt.payload.mode }),
        state: botStatus?.state ?? "running",
        mode: evt.payload.mode,
        feed_health: evt.payload.feed_health ?? botStatus?.feed_health,
      };
    }
    lastWindow.current = updated;
    currentWindow = updated;
  } else if (evt.event_type === "live.odds_tick") {
    const oddsRow: LiveOddsTickRow = {
      ts: evt.ts,
      slug: evt.slug,
      up_bid: evt.payload.up.bid,
      up_ask: evt.payload.up.ask,
      down_bid: evt.payload.down.bid,
      down_ask: evt.payload.down.ask,
      mode: evt.payload.mode ?? null,
      feed_health: evt.payload.feed_health ?? null,
    };
    const prevOdds = oddsRing.current;
    const sameWindow = prevOdds.length === 0 || prevOdds[0]!.slug === evt.slug;
    oddsRing.current = (sameWindow ? [oddsRow, ...prevOdds] : [oddsRow]).slice(0, PRICE_RING_SIZE);

    const lw = lastWindow.current && lastWindow.current.slug === evt.slug
      ? lastWindow.current
      : {
          slug: evt.slug,
          window_start: evt.ts,
          window_end: evt.ts,
          secs_left: 0,
          btc_price: "0",
          odds: { up: { bid: null, ask: null }, down: { bid: null, ask: null } },
          config_version_id: evt.config_version_id ?? "",
          active_order_id: null,
        };
    const updated: LiveWindow = {
      ...lw,
      config_version_id: evt.config_version_id ?? lw.config_version_id,
      odds: {
        up: { bid: evt.payload.up.bid, ask: evt.payload.up.ask },
        down: { bid: evt.payload.down.bid, ask: evt.payload.down.ask },
      },
    };
    if (evt.payload.mode) {
      botStatus = {
        ...(botStatus ?? { state: "running", mode: evt.payload.mode }),
        state: botStatus?.state ?? "running",
        mode: evt.payload.mode,
        feed_health: evt.payload.feed_health ?? botStatus?.feed_health,
      };
    }
    lastWindow.current = updated;
    currentWindow = updated;
  } else if (evt.event_type === "live.decision") {
    const row: LiveDecisionRow = {
      ts: evt.ts,
      slug: evt.slug,
      outcome: evt.payload.outcome,
      direction: evt.payload.direction ?? null,
      favorite_odds: evt.payload.favorite_odds ?? null,
      matching_candles: evt.payload.matching_candles ?? null,
      candles_evaluated: evt.payload.candles_evaluated ?? null,
      block_reason: evt.payload.block_reason ?? null,
      checks: evt.payload.checks.map((c) => {
        const extra = c as unknown as Record<string, unknown>;
        const perMinuteRaw = extra.per_minute;
        const perMinute: LiveDecisionPerMinute[] | null = Array.isArray(perMinuteRaw)
          ? perMinuteRaw
              .filter((row): row is Record<string, unknown> => row != null && typeof row === "object")
              .map((row) => ({
                minute: Number(row.minute ?? 0),
                open: typeof row.open === "string" ? row.open : row.open == null ? null : String(row.open),
                close: typeof row.close === "string" ? row.close : row.close == null ? null : String(row.close),
                delta: typeof row.delta === "string" ? row.delta : String(row.delta ?? "0"),
                dir: (row.dir === "UP" || row.dir === "DOWN" || row.dir === "FLAT") ? row.dir : "FLAT",
                samples: Number(row.samples ?? 0),
                is_live: Boolean(row.is_live),
                // Legacy bot builds (pre per-minute expansion) only
                // emitted per_minute for the evaluated tail and
                // omitted the `evaluated` flag. Treat a missing flag
                // as `true` so older payloads render as before.
                evaluated: row.evaluated === undefined ? true : Boolean(row.evaluated),
                matches: Boolean(row.matches),
              }))
          : null;
        const dirEval = extra.direction_evaluated;
        return {
          name: c.name,
          passed: c.passed,
          value: c.value,
          threshold: c.threshold,
          detail: c.detail ?? null,
          per_minute: perMinute,
          direction_evaluated: dirEval === "UP" || dirEval === "DOWN" ? dirEval : null,
        };
      }),
    };
    decRing.current = [row, ...decRing.current].slice(0, RING_SIZE);
  } else if (evt.event_type === "live.order_event") {
    const row: LiveOrderRow = {
      ts: evt.ts,
      slug: evt.slug,
      order_id: evt.payload.order_id,
      phase: evt.payload.phase,
      direction: evt.payload.direction,
      price: evt.payload.price ?? null,
      pnl_usdc: evt.payload.pnl_usdc ?? null,
    };
    ordRing.current = [row, ...ordRing.current].slice(0, RING_SIZE);
    if (lastWindow.current && lastWindow.current.slug === evt.slug) {
      // Only BUY-side entry events populate `active_order_id`. Sell
      // orders — TP GTC_LIMIT limits, momentum-sell, manual/SL exits —
      // must NOT overwrite the entry slot; otherwise the Live page
      // would render the resting sell price as the "Bought @ …" price
      // (e.g. a $0.86 TP order showing "Bought UP @ 86%" instead of
      // the actual entry fill).
      //
      // Only "abort"-style phases clear the entry slot. Early sells
      // (sell_filled / resolved-after-TP) keep the slot populated so
      // the Live page can keep displaying the bought position until
      // the window itself rolls over.
      const sellPhases = new Set(["sell_intent", "sell_submitted", "sell_filled"]);
      const side = evt.payload.side;
      const isSellEvent = side === "SELL" || sellPhases.has(evt.payload.phase);
      if (!isSellEvent) {
        const terminalPhases = new Set(["cancelled", "rejected", "failed", "expired"]);
        const activeOrderId = terminalPhases.has(evt.payload.phase) ? null : evt.payload.order_id;
        const updated: LiveWindow = {
          ...lastWindow.current,
          active_order_id: activeOrderId,
        };
        lastWindow.current = updated;
        currentWindow = updated;
      }
    }
  }
  return {
    ...prev,
    botStatus,
    currentWindow,
    priceTicks: [...priceRing.current],
    oddsTicks: [...oddsRing.current],
    decisions: [...decRing.current],
    orders: [...ordRing.current],
    isOffline: false,
  };
}
